// frontend/src/components/ContactAccordion.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-11-14
// Updated:      2026-09-14 10:38 BST
//
// Description:
//   Theme-aware accordion to display a company's contacts inline (used by AdvancedCustomerListWidget).
//   • Shows ALL contact fields (aligned with AdvancedContactListWidget).
//   • Column Chooser (portaled), drag-to-reorder headers, and click-to-sort.
//   • Right-aligned toolbar: Column Chooser + Search.
//   • Per-row Edit button calls onEdit?.(contact) — same as AdvancedContactListWidget.
//     If not supplied, it broadcasts window 'anpr:contacts:edit' (fallback).
//   • “Comms” column with WhatsApp / Email / SMS icons (transparent background).
//
// Messaging Consolidation (2026-09-14):
//   • Adds parent-owned onNewEmail / onNewSms / onNewWhatsApp callbacks so this
//     customer-contact surface can launch the same canonical channel workflows used
//     elsewhere in Vision.
//   • New-message callback context carries contactId + customerId with jobId = null.
//   • Comms buttons now open the canonical EmailMessageModal, SmsMessageModal and
//     WhatsAppTemplateSelectorModal/WhatsAppMessageModal when parent callbacks are
//     not yet supplied. There is no private/fake messaging modal fallback.
//   • Parent onNewEmail / onNewSms / onNewWhatsApp callbacks still take precedence
//     once the surrounding customer/contact page is consolidated.
//
// Mobile Responsiveness (2026-09-14):
//   • Presentation-only hardening. Q-21 preference hydration/persistence, filtering,
//     sorting, drag/drop ordering, edit fallback events and data rendering are unchanged.
//   • The SAME desktop table rows reflow into labelled contact cards below 768px.
//   • Mobile Sort By + direction controls are exposed because desktop headers are hidden.
//   • Column chooser and toolbar controls use touch-safe sizing/reflow.
//   • No ResizeObserver, ancestor height mutation, duplicate mobile tree, new data fetch,
//     or persistence/effect rewrite has been introduced.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ModalWrapper from "./ModalWrapper";
import EmailMessageModal from "./EmailMessageModal";
import SmsMessageModal from "./SmsMessageModal";
import WhatsAppMessageModal from "./WhatsAppMessageModal";
import WhatsAppTemplateSelectorModal from "./WhatsAppTemplateSelectorModal";
import { FaWhatsapp, FaEnvelope, FaSms } from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import api from "../api/axiosInstance";

function Tooltip({ content, children }) {
  const title = typeof content === "string" ? content : "";
  return (
    <span
      title={title}
      aria-label={title}
      style={{ display: "inline-flex", alignItems: "center" }}
    >
      {children}
    </span>
  );
}

// ---------- Helpers ----------
const storageKey = (userId, tenantId) =>
  `anpr_contact_columns_${tenantId}_${userId}`;

// Full set of columns consistent with AdvancedContactListWidget
const CONTACT_COLUMNS = [
  { key: "title", label: "Title" },
  { key: "first_name", label: "First Name" },
  { key: "last_name", label: "Last Name" },
  { key: "department", label: "Department" },
  { key: "job_title", label: "Job Title" },
  { key: "email", label: "Email" },
  { key: "secondary_email", label: "Secondary Email" },
  { key: "phone_number", label: "Main Phone" },
  { key: "mobile_number", label: "Mobile" },
  { key: "direct_line", label: "Direct Line" },
  { key: "extension", label: "Ext." },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "preferred_contact_method", label: "Preferred Contact" },
  { key: "status", label: "Status" },
  { key: "role", label: "Role" },
  { key: "access_level", label: "Access Level" },
  { key: "portal_access", label: "Portal Access" },
  { key: "is_primary", label: "Primary" },
  { key: "is_billing_contact", label: "Billing" },
  { key: "is_technical_contact", label: "Technical" },
  { key: "customer_id", label: "Company (ID)" },
  { key: "notes", label: "Notes" },
];

const DEFAULT_VISIBLE_ORDER = [
  "first_name",
  "last_name",
  "email",
  "phone_number",
  "mobile_number",
  "status",
  "role",
];

const SEARCH_FIELDS = [
  "first_name",
  "last_name",
  "email",
  "secondary_email",
  "phone_number",
  "mobile_number",
  "direct_line",
  "whatsapp",
  "department",
  "job_title",
  "role",
  "access_level",
  "preferred_contact_method",
  "status",
  "notes",
];

const fmt = (row, key) => {
  const v = row?.[key];
  if (
    key === "portal_access" ||
    key === "is_primary" ||
    key === "is_billing_contact" ||
    key === "is_technical_contact"
  ) {
    return v ? "Yes" : "No";
  }
  if (v === null || typeof v === "undefined") return "";
  return String(v);
};

const filterAndSort = (items, q, fields, sortField, ascending) => {
  const text = (q || "").trim().toLowerCase();
  let out = Array.isArray(items) ? items.slice() : [];
  if (text) {
    out = out.filter((row) =>
      fields.some((f) => String(row?.[f] ?? "").toLowerCase().includes(text))
    );
  }
  if (sortField) {
    out.sort((a, b) => {
      const av = String(a?.[sortField] ?? "").toLowerCase();
      const bv = String(b?.[sortField] ?? "").toLowerCase();
      if (av < bv) return ascending ? -1 : 1;
      if (av > bv) return ascending ? 1 : -1;
      return 0;
    });
  }
  return out;
};

// ---------- Column Chooser Modal (PORTALED) ----------
const ColumnChooserModal = ({
  open,
  onClose,
  allColumns,
  visibilityMap,
  setVisibilityMap,
  order,
  setOrder,
  resetToDefault,
}) => {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const handleToggle = (key) => {
    setVisibilityMap((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const moveColumn = (key, direction) => {
    setOrder((prev) => {
      const current = Array.isArray(prev) ? prev.slice() : [];
      const fromIndex = current.indexOf(key);
      const toIndex = fromIndex + direction;

      if (fromIndex < 0 || toIndex < 0 || toIndex >= current.length) {
        return current;
      }

      const next = current.slice();
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const content = (
    <ModalWrapper onClose={onClose}>
      <h3 className="text-lg font-semibold mb-3">Choose Columns</h3>
      <p className="mb-3 text-sm text-gray-600">
        Tick the columns you want to see.
        <span className="hidden md:inline"> Drag headers in the table to change the order.</span>
        <span className="md:hidden"> Use the arrows below to change the order.</span>
      </p>

      <div className="grid max-h-[55dvh] grid-cols-1 gap-2 overflow-auto overscroll-contain pr-1 sm:grid-cols-2 md:max-h-80 md:grid-cols-3 md:pr-2">
        {order.map((key, index) => {
          const col = allColumns.find((c) => c.key === key);
          if (!col) return null;

          return (
            <div
              key={key}
              className="flex min-h-[48px] items-center justify-between gap-2 rounded border border-gray-200 px-2 py-1 md:min-h-0 md:border-0 md:px-0 md:py-0"
            >
              <label className="flex min-h-[44px] min-w-0 flex-1 items-center gap-2 text-sm md:min-h-0">
                <input
                  type="checkbox"
                  checked={!!visibilityMap[key]}
                  onChange={() => handleToggle(key)}
                  className="h-5 w-5 flex-none md:h-auto md:w-auto"
                />
                <span className="min-w-0 break-words">{col.label}</span>
              </label>

              <div className="flex flex-none gap-1 md:hidden">
                <button
                  type="button"
                  onClick={() => moveColumn(key, -1)}
                  disabled={index === 0}
                  className="flex h-11 w-11 items-center justify-center rounded border border-gray-300 bg-white text-base font-bold disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label={`Move ${col.label} up`}
                  title="Move up"
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => moveColumn(key, 1)}
                  disabled={index === order.length - 1}
                  className="flex h-11 w-11 items-center justify-center rounded border border-gray-300 bg-white text-base font-bold disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label={`Move ${col.label} down`}
                  title="Move down"
                >
                  ↓
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-2 sm:flex sm:justify-between">
        <button
          onClick={resetToDefault}
          className="min-h-[44px] bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-sm font-semibold py-2 px-3 rounded shadow"
        >
          Reset to Default
        </button>

        <button
          onClick={onClose}
          className="min-h-[44px] bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white text-sm font-semibold py-2 px-3 rounded shadow"
        >
          Done
        </button>
      </div>
    </ModalWrapper>
  );

  const portalTarget = document.getElementById("modal-root") || document.body;
  return createPortal(content, portalTarget);
};

const getContactDisplayName = (contact) =>
  [contact?.first_name, contact?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim() ||
  contact?.email ||
  (contact?.id ? `Contact #${contact.id}` : "Contact");

const getContactPhone = (contact) =>
  contact?.whatsapp ||
  contact?.mobile_number ||
  contact?.phone_number ||
  "";

const normaliseUkToE164 = (raw) => {
  const value = String(raw || "")
    .trim()
    .replace(/[\s\-()]/g, "");

  if (!value) return "";
  if (value.startsWith("+")) return value;
  if (value.startsWith("00")) return `+${value.slice(2)}`;
  if (value.startsWith("0")) return `+44${value.slice(1)}`;
  if (value.startsWith("44")) return `+${value}`;
  return value;
};

const renderTemplateText = (textValue, placeholders) => {
  let out = String(textValue || "");

  Object.entries(placeholders || {}).forEach(([key, value]) => {
    const replacement = String(value ?? "");
    out = out.replaceAll(`{{${key}}}`, replacement);
    out = out.replaceAll(`{${key}}`, replacement);
  });

  return out;
};

const pickTemplateBaseText = (payload) => {
  const rawTemplate = payload?.raw_template;
  const dbTemplate = rawTemplate?.db || rawTemplate?.raw?.db || null;
  const providerTemplate =
    rawTemplate?.provider || rawTemplate?.raw?.provider || null;

  const candidates = [
    payload?.template_preview_text,
    dbTemplate?.body_preview,
    dbTemplate?.body_text,
    dbTemplate?.preview,
    dbTemplate?.body,
    providerTemplate?.preview,
    providerTemplate?.body_text,
    providerTemplate?.body_preview,
    providerTemplate?.body,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }

  return "";
};

// ---------- Main Component ----------
export default function ContactAccordion({
  customerId,
  customerName,
  contacts = [],
  startOpen = true,
  onEdit, // IMPORTANT: same prop name as AdvancedContactListWidget
  onNewEmail,
  onNewSms,
  onNewWhatsApp,
}) {
  const [open, setOpen] = useState(!!startOpen);

  // Toolbar state
  const [searchQuery, setSearchQuery] = useState("");
  const { user } = useAuth();

  // Q-21: canonical authenticated scope from AuthContext (/api/users/me).
  // Do not decode the JWT locally and do not create fallback unknownTenant keys.
  const userId = user?.id ?? null;
  const tenantId = user?.tenant_id ?? null;

  const persistKey = useMemo(() => {
    if (userId === null || tenantId === null) return null;
    return storageKey(String(userId), String(tenantId));
  }, [userId, tenantId]);

  const defaultVisibilityMap = () => {
    const map = {};
    CONTACT_COLUMNS.forEach(
      (c) => (map[c.key] = DEFAULT_VISIBLE_ORDER.includes(c.key))
    );
    return map;
  };

  const defaultOrder = () => {
    const defaults = DEFAULT_VISIBLE_ORDER.slice();
    const rest = CONTACT_COLUMNS.map((c) => c.key).filter(
      (k) => !defaults.includes(k)
    );
    return [...defaults, ...rest];
  };

  // Column preferences.
  const [visibilityMap, setVisibilityMap] = useState(() => defaultVisibilityMap());
  const [order, setOrder] = useState(() => defaultOrder());
  const activePreferenceKeyRef = useRef(null);

  // Hydrate whenever the authenticated tenant/user scope changes.
  useEffect(() => {
    activePreferenceKeyRef.current = null;

    if (!persistKey) {
      setVisibilityMap(defaultVisibilityMap());
      setOrder(defaultOrder());
      return;
    }

    let nextVisibility = defaultVisibilityMap();
    let nextOrder = defaultOrder();

    const raw = localStorage.getItem(persistKey);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);

        if (parsed?.visibilityMap && typeof parsed.visibilityMap === "object") {
          nextVisibility = { ...nextVisibility, ...parsed.visibilityMap };
        }

        if (Array.isArray(parsed?.order) && parsed.order.length) {
          const validKeys = new Set(CONTACT_COLUMNS.map((c) => c.key));
          const stored = parsed.order.filter((key) => validKeys.has(key));
          const missing = CONTACT_COLUMNS.map((c) => c.key).filter(
            (key) => !stored.includes(key)
          );
          nextOrder = [...stored, ...missing];
        }
      } catch {
        // Invalid data for this exact tenant/user scope falls back to defaults.
      }
    }

    setVisibilityMap(nextVisibility);
    setOrder(nextOrder);

    // Mark this exact authenticated key as hydrated only after state has been queued.
    // The persistence effect below runs after this effect/state update cycle.
    activePreferenceKeyRef.current = persistKey;
  }, [persistKey]);

  // Persist only to the exact authenticated key that has been hydrated.
  useEffect(() => {
    if (!persistKey || activePreferenceKeyRef.current !== persistKey) return;

    const data = { order, visibilityMap, v: 2 };
    localStorage.setItem(persistKey, JSON.stringify(data));
  }, [order, visibilityMap, persistKey]);

  // Sort state
  const [sortField, setSortField] = useState("first_name");
  const [ascending, setAscending] = useState(true);

  // Column chooser modal
  const [chooserOpen, setChooserOpen] = useState(false);

  // Drag & Drop header support
  const dragIndexRef = useRef(null);
  const visibleKeys = useMemo(
    () => order.filter((k) => visibilityMap[k]),
    [order, visibilityMap]
  );

  const onHeaderDragStart = (e, key) => {
    const idx = visibleKeys.indexOf(key);
    dragIndexRef.current = idx;
    e.dataTransfer.effectAllowed = "move";
  };

  const onHeaderDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const onHeaderDrop = (e, dropKey) => {
    e.preventDefault();
    const fromIdx = dragIndexRef.current;
    const toIdx = visibleKeys.indexOf(dropKey);
    if (
      fromIdx === null ||
      fromIdx === -1 ||
      toIdx === -1 ||
      fromIdx === toIdx
    )
      return;

    const newVisible = visibleKeys.slice();
    const [moved] = newVisible.splice(fromIdx, 1);
    newVisible.splice(toIdx, 0, moved);

    const hidden = order.filter((k) => !visibilityMap[k]);
    setOrder([...newVisible, ...hidden]);

    dragIndexRef.current = null;
  };

  // Sorting
  const handleSort = (field) => {
    if (field === sortField) setAscending((a) => !a);
    else {
      setSortField(field);
      setAscending(true);
    }
  };

  const renderSortIndicator = (field) => {
    if (field !== sortField) return null;
    return ascending ? " ▲" : " ▼";
  };

  const resetToDefault = () => {
    setVisibilityMap(defaultVisibilityMap());
    setOrder(defaultOrder());
    setSortField("first_name");
    setAscending(true);
  };

  const filtered = useMemo(
    () =>
      filterAndSort(
        contacts,
        searchQuery,
        SEARCH_FIELDS,
        sortField,
        ascending
      ),
    [contacts, searchQuery, sortField, ascending]
  );

  const headerSuffix = useMemo(() => {
    const count = Array.isArray(filtered) ? filtered.length : 0;
    return count
      ? ` – ${count} contact${count > 1 ? "s" : ""}`
      : " – none";
  }, [filtered]);

  // Canonical comms modal state.
  const [emailContact, setEmailContact] = useState(null);
  const [smsContact, setSmsContact] = useState(null);
  const [whatsAppTemplateContact, setWhatsAppTemplateContact] = useState(null);
  const [whatsAppThreadContact, setWhatsAppThreadContact] = useState(null);

  const buildMessagingPayload = (mode, contact) => ({
    jobId: null,
    contactId: contact?.id ?? null,
    customerId: contact?.customer_id ?? customerId ?? null,
    conversationId: null,
    messageId: null,
    channel: mode,
    mode: "new",

    // Temporary compatibility aliases while surrounding callers are consolidated.
    job_id: null,
    contact_id: contact?.id ?? null,
    customer_id: contact?.customer_id ?? customerId ?? null,
    conversation_id: null,
    message_id: null,
  });

  const openMsg = (mode, contact) => {
    const payload = buildMessagingPayload(mode, contact);

    if (mode === "email" && typeof onNewEmail === "function") {
      onNewEmail(payload);
      return;
    }

    if (mode === "sms" && typeof onNewSms === "function") {
      onNewSms(payload);
      return;
    }

    if (mode === "whatsapp" && typeof onNewWhatsApp === "function") {
      onNewWhatsApp(payload);
      return;
    }

    // Until the parent callback chain is installed, launch the SAME canonical
    // channel components directly rather than falling back to a private form.
    if (mode === "email") {
      setEmailContact(contact || null);
      return;
    }

    if (mode === "sms") {
      setSmsContact(contact || null);
      return;
    }

    if (mode === "whatsapp") {
      setWhatsAppTemplateContact(contact || null);
    }
  };

  const sendWhatsAppTemplateMessage = async (payload) => {
    const contact = whatsAppTemplateContact;
    const contactId = contact?.id ?? payload?.contact_id ?? payload?.contactId ?? null;
    const resolvedCustomerId =
      contact?.customer_id ??
      payload?.customer_id ??
      payload?.customerId ??
      customerId ??
      null;

    const rawPhone =
      payload?.contact_phone ||
      payload?.to ||
      getContactPhone(contact);

    const targetE164 = normaliseUkToE164(rawPhone);
    const targetWhatsApp = targetE164 ? `whatsapp:${targetE164}` : "";

    const templateContentSid =
      payload?.template_content_sid ||
      payload?.content_sid ||
      payload?.template_id ||
      payload?.templateId ||
      "";

    const placeholders =
      payload?.placeholders ||
      payload?.content_variables ||
      {};

    const selectorRendered =
      typeof payload?.body_text === "string"
        ? payload.body_text.trim()
        : "";

    const baseText = pickTemplateBaseText(payload);
    const fallbackRendered = baseText
      ? renderTemplateText(baseText, placeholders)
      : "";

    const previewText =
      selectorRendered ||
      fallbackRendered ||
      "(template message)";

    if (!contactId) {
      throw new Error("Missing contact_id for WhatsApp template send");
    }

    if (!targetWhatsApp) {
      throw new Error("Missing/invalid contact phone for WhatsApp template send");
    }

    if (!templateContentSid) {
      throw new Error("Missing template_content_sid for WhatsApp template send");
    }

    const conversationPayload = {
      channel: "whatsapp",
      customer_id: resolvedCustomerId,
      contact_id: contactId,
      job_id: null,
      subject:
        payload?.template_friendly_name ||
        payload?.template_name ||
        "WhatsApp Template",
    };

    const conversationResponse = await api.post(
      "/api/conversations",
      conversationPayload
    );

    const conversationId =
      conversationResponse?.data?.id ??
      conversationResponse?.data?.conversation_id ??
      conversationResponse?.data?.conversation?.id ??
      null;

    if (!conversationId) {
      throw new Error("Backend did not return a conversation id");
    }

    const sendPayload = {
      conversation_id: conversationId,
      customer_id: resolvedCustomerId,
      channel: "whatsapp",
      subject:
        payload?.template_friendly_name ||
        payload?.template_name ||
        "WhatsApp Template",
      body_text: previewText,
      body_html: null,
      metadata_json: {
        template_content_sid: templateContentSid,
        placeholders,
        template_preview_text: previewText,
        template_db_id: payload?.template_db_id ?? null,
        template_name: payload?.template_name ?? null,
        template_friendly_name: payload?.template_friendly_name ?? null,
        job_id: null,
        job_ref: "",
        source: payload?.source ?? "template_selector",
        to_whatsapp_normalised: targetWhatsApp,
        to_phone_normalised: targetE164,
        to_phone_ui: rawPhone,
      },
    };

    await api.post(
      `/api/conversations/${conversationId}/send`,
      sendPayload
    );

    setWhatsAppTemplateContact(null);
    setWhatsAppThreadContact({
      ...contact,
      __conversationId: conversationId,
    });
  };

  // Unified Edit click
  const invokeEdit = (contact) => {
    if (typeof onEdit === "function") {
      try {
        onEdit(contact);
      } catch (err) {
        console.error("[ContactAccordion] onEdit threw:", err);
      }
      return;
    }

    try {
      const events = [
        "anpr:contacts:edit",
        "anpr:contact:edit",
        "anpr:editContact",
      ];
      events.forEach((name) =>
        window.dispatchEvent(new CustomEvent(name, { detail: contact }))
      );
    } catch (err) {
      console.error("[ContactAccordion] dispatch failed:", err);
    }
  };

  useEffect(() => {
    console.debug(
      "[ContactAccordion] mounted — onEdit is",
      typeof onEdit === "function" ? "function ✅" : String(onEdit)
    );
  }, [onEdit]);

  return (
    <div className="vx-contact-accordion border rounded-xl overflow-hidden">
      <style>{`
        @media (max-width: 767px) {
          .vx-contact-accordion-table-wrap {
            overflow-x: hidden !important;
            border: 0 !important;
            border-radius: 0 !important;
          }

          .vx-contact-accordion-table,
          .vx-contact-accordion-table tbody {
            display: block;
            width: 100%;
            min-width: 0;
          }

          .vx-contact-accordion-table thead {
            display: none;
          }

          .vx-contact-accordion-table tr.vx-contact-row {
            display: grid;
            width: 100%;
            min-width: 0;
            box-sizing: border-box;
            margin: 0 0 10px 0;
            padding: 10px 12px;
            border: 1px solid #e5e7eb;
            border-radius: 10px;
            background: #fff;
          }

          .vx-contact-accordion-table tr.vx-contact-row:last-child {
            margin-bottom: 0;
          }

          .vx-contact-accordion-table tr.vx-contact-row td {
            display: grid;
            grid-template-columns: minmax(105px, 0.8fr) minmax(0, 1.2fr);
            gap: 10px;
            align-items: start;
            min-width: 0;
            padding: 7px 0 !important;
            border-bottom: 1px solid #f1f5f9;
            text-align: left !important;
            overflow-wrap: anywhere;
          }

          .vx-contact-accordion-table tr.vx-contact-row td:last-child {
            border-bottom: 0;
          }

          .vx-contact-accordion-table tr.vx-contact-row td::before {
            content: attr(data-label);
            min-width: 0;
            font-size: 12px;
            font-weight: 700;
            color: #6b7280;
            overflow-wrap: anywhere;
          }

          .vx-contact-accordion-table tr.vx-contact-empty-row {
            display: table-row;
          }

          .vx-contact-accordion-table tr.vx-contact-empty-row td {
            display: table-cell;
            width: 100%;
            padding: 12px !important;
          }

          .vx-contact-accordion-table tr.vx-contact-empty-row td::before {
            content: none;
          }

          .vx-contact-comms {
            justify-content: flex-start !important;
          }

          .vx-contact-comms-button {
            display: inline-flex;
            width: 44px;
            height: 44px;
            align-items: center;
            justify-content: center;
            touch-action: manipulation;
          }

          .vx-contact-edit-button {
            width: 100%;
            min-height: 44px;
          }
        }
      `}</style>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="min-h-[48px] w-full break-words px-4 py-3 text-left touch-manipulation"
        title="Toggle contacts"
        aria-expanded={open}
        style={{
          color: "var(--accent-color, #ff7a00)",
          background: "rgba(0,0,0,0.02)",
        }}
      >
        👤 Contacts for {customerName || `Customer #${customerId}`}{" "}
        {open ? "▾" : "▸"}{" "}
        <span className="text-gray-500 text-sm">{headerSuffix}</span>
      </button>

      {open && (
        <div className="grid gap-3 border-t p-3 sm:p-4">
          {/* Toolbar (right-aligned: chooser + search) */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
            <button
              type="button"
              onClick={() => setChooserOpen(true)}
              className="min-h-[44px] w-full bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-xs font-semibold px-3 py-2 rounded shadow sm:w-auto"
              title="Choose Columns"
            >
              Column Chooser
            </button>
            <input
              type="text"
              placeholder="Search contacts…"
              className="min-h-[44px] w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-black sm:w-56"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-[minmax(0,1fr)_48px] gap-2 md:hidden">
            <select
              value={sortField}
              onChange={(e) => {
                setSortField(e.target.value);
                setAscending(true);
              }}
              className="min-h-[44px] min-w-0 rounded border border-gray-300 bg-white px-3 py-2 text-sm text-black"
              aria-label="Sort contacts by"
            >
              {visibleKeys.map((key) => {
                const col = CONTACT_COLUMNS.find((c) => c.key === key);
                if (!col) return null;
                return (
                  <option key={key} value={key}>
                    Sort: {col.label}
                  </option>
                );
              })}
            </select>

            <button
              type="button"
              onClick={() => setAscending((value) => !value)}
              className="flex min-h-[44px] items-center justify-center rounded bg-btnGeneralNormal text-base font-bold text-white shadow"
              aria-label={ascending ? "Sort descending" : "Sort ascending"}
              title={ascending ? "Sort descending" : "Sort ascending"}
            >
              {ascending ? "▲" : "▼"}
            </button>
          </div>

          {/* Table */}
          <div className="vx-contact-accordion-table-wrap overflow-auto border rounded-lg">
            <table className="vx-contact-accordion-table min-w-full text-sm">
              <thead>
                <tr className="text-left border-b bg-gray-50 select-none">
                  {visibleKeys.map((key) => {
                    const col = CONTACT_COLUMNS.find((c) => c.key === key);
                    if (!col) return null;
                    return (
                      <th
                        key={key}
                        className="py-2 px-3 cursor-move"
                        draggable
                        onDragStart={(e) => onHeaderDragStart(e, key)}
                        onDragOver={onHeaderDragOver}
                        onDrop={(e) => onHeaderDrop(e, key)}
                        onClick={() => handleSort(key)}
                        title="Drag to reorder • Click to sort"
                      >
                        {col.label}
                        {renderSortIndicator(key)}
                      </th>
                    );
                  })}
                  {/* Fixed columns at end */}
                  <th className="py-2 px-3 text-center">Comms</th>
                  <th className="py-2 px-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {!filtered || filtered.length === 0 ? (
                  <tr className="vx-contact-empty-row">
                    <td
                      colSpan={visibleKeys.length + 2}
                      className="py-3 px-3"
                    >
                      No contacts found for this customer.
                    </td>
                  </tr>
                ) : (
                  filtered.map((c) => (
                    <tr key={c.id} className="vx-contact-row border-b last:border-0">
                      {visibleKeys.map((colKey) => (
                        <td
                          key={colKey}
                          data-label={
                            CONTACT_COLUMNS.find((column) => column.key === colKey)?.label ||
                            colKey
                          }
                          className="py-2 px-3 align-top"
                        >
                          {fmt(c, colKey)}
                        </td>
                      ))}

                      {/* Comms icons — transparent, minimal padding, theme primary color */}
                      <td data-label="Comms" className="py-2 px-3 align-top">
                        <div className="vx-contact-comms flex items-center justify-center gap-1 sm:gap-3">
                          <Tooltip content="Send WhatsApp">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openMsg("whatsapp", c);
                              }}
                              className="vx-contact-comms-button flex h-11 w-11 touch-manipulation items-center justify-center rounded-full bg-green-600 text-xl text-white shadow hover:bg-green-700 active:bg-green-800"
                              aria-label="Send WhatsApp"
                            >
                              <FaWhatsapp size={18} />
                            </button>
                          </Tooltip>

                          <Tooltip content="Send Email">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openMsg("email", c);
                              }}
                              className="vx-contact-comms-button flex h-11 w-11 touch-manipulation items-center justify-center rounded-full bg-btnActionNormal text-xl text-white shadow hover:bg-btnActionHover active:bg-btnActionClick"
                              aria-label="Send Email"
                            >
                              <FaEnvelope size={18} />
                            </button>
                          </Tooltip>

                          <Tooltip content="Send SMS">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openMsg("sms", c);
                              }}
                              className="vx-contact-comms-button flex h-11 w-11 touch-manipulation items-center justify-center rounded-full bg-btnGeneralNormal text-xl text-white shadow hover:bg-btnGeneralHover active:bg-btnGeneralClick"
                              aria-label="Send SMS"
                            >
                              <FaSms size={18} />
                            </button>
                          </Tooltip>
                        </div>
                      </td>

                      {/* Actions */}
                      <td data-label="Actions" className="py-2 px-3 text-center align-top">
                        <button
                          type="button"
                          onClick={(e) => {
                            e?.stopPropagation?.();
                            console.debug(
                              "[ContactAccordion] Edit clicked",
                              c
                            );
                            invokeEdit(c);
                          }}
                          className="vx-contact-edit-button bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white text-xs font-body px-3 py-2 rounded shadow transition-colors duration-200 ease-in-out"
                          title="Edit Contact"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="text-xs text-gray-500">
            Tip: Columns & sorting mirror Advanced Contact List for consistency.
          </div>
        </div>
      )}

      {/* Column chooser */}
      <ColumnChooserModal
        open={open && chooserOpen}
        onClose={() => setChooserOpen(false)}
        allColumns={CONTACT_COLUMNS}
        visibilityMap={visibilityMap}
        setVisibilityMap={setVisibilityMap}
        order={order}
        setOrder={setOrder}
        resetToDefault={resetToDefault}
      />

      {/* Canonical channel modals */}
      {emailContact && (
        <EmailMessageModal
          open={true}
          onClose={() => setEmailContact(null)}
          jobId={null}
          contactId={emailContact.id}
          customerId={emailContact.customer_id ?? customerId ?? null}
          contactEmail={emailContact.email || emailContact.secondary_email || ""}
          defaultTo={emailContact.email || emailContact.secondary_email || ""}
        />
      )}

      {smsContact && (
        <SmsMessageModal
          open={true}
          onClose={() => setSmsContact(null)}
          jobId={null}
          contactId={smsContact.id}
          contactName={getContactDisplayName(smsContact)}
          contactPhone={getContactPhone(smsContact)}
          defaultTo={getContactPhone(smsContact)}
        />
      )}

      {whatsAppTemplateContact && (
        <WhatsAppTemplateSelectorModal
          open={true}
          onClose={() => setWhatsAppTemplateContact(null)}
          contactId={whatsAppTemplateContact.id}
          contactName={getContactDisplayName(whatsAppTemplateContact)}
          contactPhone={getContactPhone(whatsAppTemplateContact)}
          jobId={null}
          jobRef=""
          tenantId={null}
          onConfirm={async (payload) => {
            try {
              await sendWhatsAppTemplateMessage(payload);
            } catch (err) {
              console.error(
                "[ContactAccordion] WhatsApp template send failed",
                err?.response?.data || err?.message || err
              );

              const detail =
                err?.response?.data?.detail ||
                err?.response?.data?.message ||
                err?.message ||
                "Failed to send WhatsApp template message";

              alert(
                typeof detail === "string"
                  ? detail
                  : JSON.stringify(detail, null, 2)
              );
            }
          }}
        />
      )}

      {whatsAppThreadContact && (
        <WhatsAppMessageModal
          open={true}
          onClose={() => setWhatsAppThreadContact(null)}
          jobId={null}
          contactId={whatsAppThreadContact.id}
          contactName={getContactDisplayName(whatsAppThreadContact)}
          contactPhone={getContactPhone(whatsAppThreadContact)}
          conversationId={whatsAppThreadContact.__conversationId ?? null}
        />
      )}
    </div>
  );
}
